import { useState, useEffect, useCallback } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard,
  Megaphone,
  Video,
  FileQuestion,
  Radio,
  Mail,
  PhoneCall,
  Swords,
  PhoneOutgoing,
  XCircle,
  UserSearch,
  Wallet,
  ChevronDown,
  LogOut,
  Sun,
  Moon,
  Crown,
  Bell,
  Menu,
  ArrowLeftRight,
  Settings,
  TrendingUp,
  Phone,
  Target,
  Heart,
  Tag,
  DollarSign,
  Users,
  UsersRound,
  Filter,
  Shield,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MarketingLayout({ children }) {
  const { user, logout, accounts, switchAccount } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [theme, setTheme] = useState(() => localStorage.getItem("warmap-theme") || "dark");
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const currentAccount = accounts?.find((a) => a.account_id === user?.account_id);

  const closeSidebar = () => setSidebarOpen(false);

  useEffect(() => {
    document.documentElement.classList.toggle("light", theme === "light");
    localStorage.setItem("warmap-theme", theme);
  }, [theme]);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await fetch(`${API}/notifications`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
        setUnreadCount(data.filter(n => !n.read).length);
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (e) {
      console.error("Logout error:", e);
    }
    logout();
    navigate("/login");
  };

  const navItems = [
    { icon: LayoutDashboard, label: "Command Center", path: "/marketing" },
    { icon: Megaphone, label: "Ad Warfare", path: "/marketing/ad-warfare" },
    { icon: Video, label: "VSL Funnel", path: "/marketing/vsl-funnel" },
    { icon: FileQuestion, label: "Typeform Insights", path: "/marketing/typeform-intel" },
    { icon: Radio, label: "Webinar Ops", path: "/marketing/webinar-ops" },
    { icon: Mail, label: "Email & SMS", path: "/marketing/email-sms" },
    { icon: PhoneCall, label: "Pre-Call Ops", path: "/marketing/pre-call-ops" },
    { icon: Swords, label: "Sales Warfare", path: "/marketing/sales-warfare" },
    { icon: PhoneOutgoing, label: "Post-Call Ops", path: "/marketing/post-call-ops" },
    { icon: XCircle, label: "Cancellations", path: "/marketing/cancellations" },
    { icon: UserSearch, label: "Lead Intel", path: "/marketing/lead-intel" },
    { icon: Wallet, label: "Revenue Tracker", path: "/marketing/war-chest" },
    { icon: TrendingUp, label: "Lead Flow", path: "/marketing/lead-flow" },
    { icon: Phone, label: "Setter Performance", path: "/marketing/setter-performance" },
    { icon: Target, label: "Closer Performance", path: "/marketing/closer-performance" },
    { icon: Heart, label: "Nurture", path: "/marketing/nurture" },
    { icon: Tag, label: "Offer Intel", path: "/marketing/offer-intel" },
    { icon: DollarSign, label: "Revenue Intel", path: "/marketing/revenue-intel" },
    { icon: Users, label: "Client Intel", path: "/marketing/client-intel" },
    { icon: UsersRound, label: "Team Intel", path: "/marketing/team-intel" },
    { icon: Filter, label: "Funnel Intel", path: "/marketing/funnel-intel" },
    { icon: Settings, label: "Integrations", path: "/marketing/integrations" },
  ];

  return (
    <div className={`flex min-h-screen ${theme === "light" ? "bg-gray-100" : "bg-[#0A0A0A]"}`}>
      {/* Mobile Header */}
      <div className={`lg:hidden fixed top-0 left-0 right-0 z-50 h-14 flex items-center justify-between px-4 ${theme === "light" ? "bg-white border-gray-200" : "bg-[#0f0f10] border-white/5"} border-b`}>
        <button
          onClick={() => setSidebarOpen(true)}
          className={`p-2 rounded-lg ${theme === "light" ? "hover:bg-gray-100" : "hover:bg-white/10"}`}
          data-testid="marketing-mobile-menu-btn"
        >
          <Menu className={`w-6 h-6 ${theme === "light" ? "text-gray-900" : "text-white"}`} />
        </button>
        <div className="flex items-center gap-2">
          <Crown className="w-6 h-6 text-emerald-600" />
          <span className={`text-lg font-bold font-heading italic ${theme === "light" ? "text-gray-900" : "text-white"}`}>WarMap</span>
          <span className="text-xs text-emerald-400 font-medium">MKT</span>
        </div>
        <button className="relative p-2 rounded-lg">
          <Bell className={`w-5 h-5 ${theme === "light" ? "text-gray-600" : "text-gray-400"}`} />
          {unreadCount > 0 && (
            <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 h-screen w-64 ${theme === "light" ? "bg-white border-gray-200" : "bg-[#0f0f10] border-white/5"} border-r flex flex-col z-50 transform transition-transform duration-300 ease-in-out ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        {/* Logo & Dashboard Switcher */}
        <div className={`h-16 flex items-center justify-between px-4 border-b ${theme === "light" ? "border-gray-200" : "border-white/5"}`}>
          <div className="flex items-center gap-2">
            <Crown className="w-8 h-8 text-emerald-600" />
            <div>
              <span className={`text-xl font-bold font-heading italic ${theme === "light" ? "text-gray-900" : "text-white"}`}>WarMap</span>
              <span className="ml-1 text-xs text-emerald-400 font-medium">MKT</span>
            </div>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            className={`p-2 rounded-lg transition-colors ${theme === "light" ? "hover:bg-gray-100 text-gray-600" : "hover:bg-white/10 text-gray-400"}`}
            title="Switch to Sales Dashboard"
            data-testid="switch-to-sales-btn"
          >
            <ArrowLeftRight className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 px-3 py-4">
          <nav className="space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={closeSidebar}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? theme === "light" ? "bg-emerald-50 text-emerald-800" : "bg-white/10 text-white"
                      : theme === "light" ? "text-gray-600 hover:text-gray-900 hover:bg-gray-100" : "text-gray-400 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </ScrollArea>

        {/* Theme Toggle */}
        <div className={`px-4 py-3 border-t ${theme === "light" ? "border-gray-200" : "border-white/5"}`}>
          <button
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              theme === "light" ? "text-gray-600 hover:text-gray-900 hover:bg-gray-100" : "text-gray-400 hover:text-white hover:bg-white/5"
            }`}
          >
            {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            {theme === "dark" ? "Light Mode" : "Dark Mode"}
          </button>
        </div>

        {/* User Profile */}
        <div className={`p-4 border-t ${theme === "light" ? "border-gray-200" : "border-white/5"}`}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors ${theme === "light" ? "hover:bg-gray-100" : "hover:bg-white/5"}`}>
                <Avatar className="w-10 h-10">
                  <AvatarImage src={user?.picture || user?.avatar_url} />
                  <AvatarFallback className={`${theme === "light" ? "bg-gray-200 text-gray-700" : "bg-white/10 text-white"}`}>
                    {user?.name?.charAt(0) || "U"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className={`text-sm font-medium truncate ${theme === "light" ? "text-gray-900" : "text-white"}`}>
                    {user?.name || "User"}
                  </p>
                  <p className={`text-xs truncate ${theme === "light" ? "text-gray-500" : "text-gray-400"}`}>
                    {currentAccount?.name || "Account"}
                  </p>
                </div>
                <ChevronDown className={`w-4 h-4 ${theme === "light" ? "text-gray-400" : "text-gray-500"}`} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className={`w-56 ${theme === "light" ? "bg-white border-gray-200" : "bg-[#1A1A1A] border-white/10"}`}>
              {accounts?.length > 1 && (
                <>
                  <div className={`px-2 py-1.5 text-xs font-semibold ${theme === "light" ? "text-gray-500" : "text-gray-400"}`}>
                    Switch Account
                  </div>
                  {accounts.map((acc) => (
                    <DropdownMenuItem
                      key={acc.account_id}
                      onClick={() => switchAccount(acc.account_id)}
                      className={`cursor-pointer ${
                        acc.account_id === user?.account_id
                          ? "bg-emerald-500/20 text-emerald-400"
                          : theme === "light" ? "text-gray-700" : "text-white"
                      }`}
                    >
                      {acc.name}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator className={theme === "light" ? "bg-gray-200" : "bg-white/10"} />
                </>
              )}
              {user?.role === "superadmin" && (
                <DropdownMenuItem
                  onClick={() => navigate("/superadmin/dashboard")}
                  className="cursor-pointer text-red-400"
                  data-testid="mkt-superadmin-btn"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Super Admin
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => navigate("/settings")}
                className={`cursor-pointer ${theme === "light" ? "text-gray-700" : "text-white"}`}
              >
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleLogout}
                className="cursor-pointer text-red-400"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`lg:ml-64 flex-1 min-h-screen pt-14 lg:pt-0 ${theme === "light" ? "bg-gray-50 text-gray-900" : ""}`}>
        <div className="p-4 md:p-6 lg:p-8 page-enter">{children}</div>
      </main>
    </div>
  );
}
